import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Modal, SearchableDropdown } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  ADDRESS_IMPORT_TEMPLATE_FILENAME,
  ADDRESS_IMPORT_TEMPLATE_HEADERS,
  AddressImportCommitResult,
  AddressImportException,
  AddressImportPreviewResult,
  addressesAPI,
} from '@/services/api/addresses';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';

type Stage = 'pick' | 'preview' | 'committing';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: (result: AddressImportCommitResult) => void;
}

const csvEscape = (v: string): string =>
  /[,"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;

// A single illustrative row so the downloaded template shows the expected shape.
const TEMPLATE_SAMPLE = [
  'Hub Jakarta Pusat',
  'Jl. Hub No. 1, Jakarta',
  '-6.2',
  '106.8',
  'Apotek Sehat Kemang',
  'Jl. Kemang Raya No. 10, Jakarta Selatan',
  '-6.26',
  '106.813',
  '8400',
];

const buildErrorCsv = (preview: AddressImportPreviewResult): Blob => {
  const lines = ['row_number,field,value,message'];
  for (const e of preview.errors) {
    lines.push(
      [
        String(e.rowNumber),
        csvEscape(e.field),
        csvEscape(e.value ?? ''),
        csvEscape(e.message),
      ].join(',')
    );
  }
  return new Blob(['﻿' + lines.join('\r\n') + '\r\n'], {
    type: 'text/csv;charset=utf-8',
  });
};

const friendlyError = (err: unknown): string =>
  err instanceof Error ? err.message : 'Unknown error';

const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
}) => {
  const [stage, setStage] = useState<Stage>('pick');
  const [clientId, setClientId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AddressImportPreviewResult | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Owning client/provider for every lane in this batch (sent to import, like
  // shipment import).
  const providers = useProvidersDropdown(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setStage('pick');
    setClientId('');
    setFile(null);
    setSubmitting(false);
    setInlineError(null);
    setPreview(null);
  }, [isOpen]);

  const clientIdNumeric = useMemo(() => {
    const n = Number(clientId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [clientId]);

  if (!isOpen) return null;

  const handleFile = (f: File | null) => {
    setFile(f);
    setInlineError(null);
  };

  const downloadTemplate = () => {
    const rows = [
      ADDRESS_IMPORT_TEMPLATE_HEADERS.map(csvEscape).join(','),
      TEMPLATE_SAMPLE.map(csvEscape).join(','),
    ];
    const blob = new Blob(['﻿' + rows.join('\r\n') + '\r\n'], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = ADDRESS_IMPORT_TEMPLATE_FILENAME;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadErrors = () => {
    if (!preview) return;
    const blob = buildErrorCsv(preview);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'address-import-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePreview = async () => {
    if (!file || !clientIdNumeric) return;
    setSubmitting(true);
    setInlineError(null);
    try {
      // dry-run returns 200 even with row errors (they live in summary/errors);
      // only file-level failures throw.
      const result = await addressesAPI.importDryRun({
        clientId: clientIdNumeric,
        file,
      });
      setPreview(result);
      setStage('preview');
    } catch (err) {
      setInlineError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const affected = preview
    ? preview.summary.toCreate + preview.summary.toUpdate
    : 0;
  const blockCommit =
    !preview || preview.summary.errors > 0 || affected === 0;

  const handleConfirm = async () => {
    if (!file || !clientIdNumeric || blockCommit) return;
    setStage('committing');
    setSubmitting(true);
    setInlineError(null);
    try {
      const res = await addressesAPI.importCommit({
        clientId: clientIdNumeric,
        file,
      });
      onImported(res);
      const parts: string[] = [];
      if (res.created > 0) parts.push(`${res.created} created`);
      if (res.updated > 0) parts.push(`${res.updated} updated`);
      showSuccessToast(
        `Imported ${res.totalRows} address lane${res.totalRows === 1 ? '' : 's'}`,
        parts.join(' · ') || 'No changes made.'
      );
      onClose();
    } catch (err) {
      // Backend rejects a dirty commit with 409 + the same preview payload.
      if (
        err instanceof AddressImportException &&
        err.code === 'IMPORT_VALIDATION_FAILED' &&
        err.preview
      ) {
        setPreview(err.preview);
        setStage('preview');
        setInlineError(err.message);
      } else {
        setStage('preview');
        setInlineError(friendlyError(err));
        showErrorToast('Import failed', friendlyError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };
  const stopDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const canPreview =
    Boolean(clientIdNumeric) &&
    Boolean(file) &&
    !submitting &&
    stage === 'pick';

  const pickFooter = (
    <div className="flex items-center justify-end gap-2">
      <Button variant="white" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={handlePreview} disabled={!canPreview}>
        {submitting ? (
          <>
            <i className="ri-loader-4-line mr-1.5 animate-spin text-[14px]" />
            Previewing…
          </>
        ) : (
          'Preview import'
        )}
      </Button>
    </div>
  );

  const previewFooter = preview && (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="white"
        onClick={() => {
          setStage('pick');
          setPreview(null);
          setInlineError(null);
        }}
        disabled={submitting}
      >
        Back
      </Button>
      <Button variant="white" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={handleConfirm} disabled={blockCommit || submitting}>
        {submitting ? (
          <>
            <i className="ri-loader-4-line mr-1.5 animate-spin text-[14px]" />
            Importing…
          </>
        ) : (
          `Confirm import${affected > 0 ? ` (${affected})` : ''}`
        )}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      icon="ri-upload-2-line"
      iconTone="primary"
      title="Import addresses from CSV"
      subtitle="Muat banyak lane sekaligus. Upsert per pasangan nama (origin + destination)."
      width="xl"
      padded={false}
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={stage === 'pick' ? pickFooter : previewFooter}
    >
      {stage === 'pick' && (
        <div className="space-y-5 p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                Client <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                placeholder="Cari client / provider…"
                options={providers.options.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                }))}
                value={clientId}
                selectedLabelFallback={providers.labelFor(clientId)}
                onChange={(next) => setClientId(String(next))}
                onSearchChange={providers.setSearch}
                disableLocalFilter
                loading={providers.loading}
                hasMore={providers.hasMore}
                onLoadMore={providers.loadMore}
                disabled={submitting}
                emptyMessage={providers.error ?? 'Tidak ada client'}
              />
            </div>
            <div className="flex items-end">
              <p className="text-[11px] leading-relaxed text-gray-500">
                Semua lane di file ini akan dimiliki oleh client ini.
              </p>
            </div>
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                CSV file <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={downloadTemplate}
                className="text-xs font-semibold tracking-tight text-primary hover:underline"
                aria-label="Download CSV template"
              >
                Download template
              </button>
            </div>
            <div
              onDrop={onDrop}
              onDragOver={stopDrag}
              onDragEnter={stopDrag}
              className="rounded-gsm border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center transition-colors hover:border-primary"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv,application/vnd.ms-excel"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
              {file ? (
                <div className="flex items-center justify-center gap-3 text-sm text-gray-700">
                  <i className="ri-file-text-line text-[20px] text-primary" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFile(null)}
                    className="ml-2 text-xs text-gray-500 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-gray-700"
                >
                  <span className="block">
                    Drag &amp; drop CSV di sini, atau
                    <span className="ml-1 font-semibold text-primary">
                      browse
                    </span>
                  </span>
                  <span className="mt-1 block text-xs text-gray-500">
                    .csv saja
                  </span>
                </button>
              )}
            </div>
            <p className="mt-2 text-[11px] text-gray-500">
              Kolom:{' '}
              <span className="font-mono">
                {ADDRESS_IMPORT_TEMPLATE_HEADERS.join(', ')}
              </span>
              . Nama + alamat + koordinat wajib; <b>Distance</b> (meter) opsional
              (default 0). Kolom tak dikenal diabaikan.
            </p>
          </div>

          {inlineError && (
            <div className="rounded-gsm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {inlineError}
            </div>
          )}
        </div>
      )}

      {(stage === 'preview' || stage === 'committing') && preview && (
        <div className="space-y-4 p-5">
          <div>
            <p className="mb-2 text-xs text-gray-500">
              {preview.summary.totalRows} baris di-parse
            </p>
            <div className="grid grid-cols-3 gap-3">
              <SummaryCard
                label="To create"
                value={preview.summary.toCreate}
                tone="text-emerald-700 bg-emerald-50 border-emerald-200"
              />
              <SummaryCard
                label="To update"
                value={preview.summary.toUpdate}
                tone="text-sky-700 bg-sky-50 border-sky-200"
              />
              <SummaryCard
                label="Errors"
                value={preview.summary.errors}
                tone="text-red-700 bg-red-50 border-red-200"
              />
            </div>
          </div>

          {preview.summary.ignoredColumns.length > 0 && (
            <div className="rounded-gsm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Ignored unknown columns:{' '}
              <span className="font-mono">
                {preview.summary.ignoredColumns.join(', ')}
              </span>
            </div>
          )}

          <div className="overflow-hidden rounded-gsm border border-gray-200">
            <ErrorsTable
              preview={preview}
              onDownload={handleDownloadErrors}
            />
          </div>

          {preview.summary.errors > 0 && (
            <div className="rounded-gsm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Import all-or-nothing — perbaiki semua error dulu sebelum confirm.
            </div>
          )}

          {inlineError && (
            <div className="rounded-gsm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {inlineError}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: number;
  tone: string;
}> = ({ label, value, tone }) => (
  <div className={`rounded-gsm border ${tone} px-4 py-3`}>
    <p className="text-[10px] font-bold uppercase tracking-[0.18em]">{label}</p>
    <p className="mt-2 text-2xl font-bold tracking-tight tabular-nums">
      {value}
    </p>
  </div>
);

const ErrorsTable: React.FC<{
  preview: AddressImportPreviewResult;
  onDownload: () => void;
}> = ({ preview, onDownload }) => {
  if (preview.summary.errors === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-gray-500">
        Tidak ada error. Siap di-import.
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
        <p className="text-[11px] text-gray-600">
          {preview.summary.errors} baris error memblokir import.
        </p>
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-1 text-xs font-semibold tracking-tight text-primary hover:underline"
        >
          <i className="ri-download-2-line text-[14px]" />
          Download error CSV
        </button>
      </div>
      <div className="max-h-[280px] overflow-auto">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-gray-50 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
            <tr>
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Field</th>
              <th className="px-3 py-2">Value</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody className="text-gray-800">
            {preview.errors.map((e, idx) => (
              <tr
                key={`${e.rowNumber}-${e.field}-${idx}`}
                className="border-t border-gray-100"
              >
                <td className="px-3 py-1.5 tabular-nums text-gray-500">
                  {e.rowNumber}
                </td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{e.field}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-gray-600">
                  {e.value || <span className="italic text-gray-400">empty</span>}
                </td>
                <td className="px-3 py-1.5 text-red-700">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportModal;
